// message-status.ts — SINGLE SOURCE OF TRUTH for message status.
// Duplicated byte-identically into all 3 repos' bt-common (hand-synced).
//
// THE single canonical message-status enum — shared by BOTH the `userMessages` model and the
// `messageLogs` model (backend + frontend). One source of truth so the enum can NEVER drift
// between collections or repos again (it previously did: the frontend message-log copy had lost
// `failed`, and `sent_pending_ack` had to be added in several places).
//
// It is the SUPERSET of both lifecycles (both models validate against it via mongoose `enum:`):
//   queueing:   pending · before-wa-send · sending
//   sent:       sent · sent_pending_ack · bt-sent
//   delivered:  confirmed · delivered · read · played · received
//   terminal:   cancelled · error · failed · expired
//
// `sent_pending_ack`: the send call returned a waMessageKey but WhatsApp's ack is still 0
// (PENDING) — NOT yet acked, so treated as not-sent (stays queued, retry-eligible). ack >= 1
// (SERVER) promotes it to `confirmed`. Sits BEFORE `confirmed` in the lifecycle.
//
// `expired`: terminal — the campaign's `timing.maxSendWindowMinutes` elapsed before the message
// could be sent (engine offline past the window); the send was skipped server-side, never attempted.
import { z } from 'zod';

// ── Canonical status vocabulary (value + human-readable description) ────
// Array order also documents lifecycle progression (terminal failures are pulled out
// separately in messageLogStatusLevel so a late failure never overrides real progress).
export const MESSAGE_STATUSES = [
  { value: 'pending',          desc: 'Accepted and waiting (scheduled or ready to send).' },
  { value: 'before-wa-send',   desc: 'Right before we hand the message to WhatsApp to send.' },
  { value: 'bt-sent',          desc: 'Blueticks has started sending the message.' },
  { value: 'sending',          desc: 'Hand-off to WhatsApp is in flight.' },
  { value: 'sent',             desc: 'Dispatched to WhatsApp.' },
  { value: 'sent_pending_ack', desc: 'Sent to WhatsApp (real waMessageKey) but ack still 0 — awaiting server ack; treated as not-yet-delivered.' },
  { value: 'confirmed',        desc: 'We received the waMessageKey / ack >= 1 — WhatsApp accepted it (really sent).' },
  { value: 'delivered',        desc: 'Delivered to the recipient device.' },
  { value: 'received',         desc: 'Double grey tick — recipient device has it.' },
  { value: 'read',             desc: 'Double blue tick — read.' },
  { value: 'played',           desc: 'Voice note played.' },
  { value: 'cancelled',        desc: 'User cancelled; the userMessage was deleted, so this lives only in the log.' },
  { value: 'error',            desc: 'The send raised an error.' },
  { value: 'failed',           desc: 'Terminal failure (carries the error).' },
  { value: 'expired',          desc: 'Terminal — max-send-window elapsed before send; skipped server-side, never attempted.' },
] as const;

export type MessageStatus = typeof MESSAGE_STATUSES[number]['value'];

// The canonical flat list/schema. `MessageStatusList` is a real value binding — mongoose
// `enum:` and zod both reference it directly.
export const MessageStatusList = MESSAGE_STATUSES.map((s) => s.value) as MessageStatus[];
export const MessageStatusSchema = z.enum(MessageStatusList as [MessageStatus, ...MessageStatus[]]);

// ── Backwards-compatible per-model aliases ─────────────────────────────
// Both userMessages and messageLogs can hold ANY status in the superset. The per-collection
// enums previously drifted apart and rejected valid statuses (`expired`, `sent_pending_ack`);
// these aliases keep existing importers of the per-model names working, all pointing at the
// single canonical superset so drift is impossible.
export const UserMessageStatusList = MessageStatusList;
export const MessageLogStatusList = MessageStatusList;
export const UserMessageStatusSchema = MessageStatusSchema;
export const MessageLogStatusSchema = MessageStatusSchema;
export type UserMessageStatus = MessageStatus;
export type MessageLogStatus = MessageStatus;

export const MESSAGE_STATUS_DESC: Record<MessageStatus, string> =
  Object.fromEntries(MESSAGE_STATUSES.map((s) => [s.value, s.desc])) as Record<MessageStatus, string>;

// Lifecycle ordering for "most-advanced-wins" derivation. Higher index = more advanced.
// Terminal failures are handled separately (below) so a later `failed`/`expired` never
// overrides actual progress (e.g. a real `read`).
export const MESSAGE_LOG_LIFECYCLE_ORDER: MessageStatus[] = [
  'pending', 'before-wa-send', 'bt-sent', 'sending', 'sent', 'sent_pending_ack',
  'confirmed', 'delivered', 'received', 'read', 'played',
];

// Terminal failure states — ranked below all progress so they never clobber a real milestone.
const TERMINAL_FAILURE_STATUSES = new Set<MessageStatus>(['failed', 'cancelled', 'error', 'expired']);

/** Numeric advancement level; HIGHER = more advanced (read > confirmed > pending).
 *  Terminal failures (failed/cancelled/error/expired) return -1 so a later failure never
 *  overrides actual progress. Unknown values return -2.
 *  `sent_pending_ack` outranks before-wa-send/failed (#148) so a real send surfaces as sent. */
export function messageLogStatusLevel(status: string): number {
  if (TERMINAL_FAILURE_STATUSES.has(status as MessageStatus)) return -1;
  const idx = MESSAGE_LOG_LIFECYCLE_ORDER.indexOf(status as MessageStatus);
  return idx === -1 ? -2 : idx;
}
